import React, { useMemo } from 'react';
import { Box, Flex, Grid, SimpleGrid, Icon, Text, Button } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  ComposedChart,
  Area,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  Files,
  ArrowSquareOut,
  UsersThree,
  CurrencyDollar,
  Receipt,
  TrendUp,
} from 'phosphor-react';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import { num, pct } from '../../format';
import { IDossie, IDossieRisco } from '../../types';
import { EmptyState } from '../../components/EmptyState';
import { InfoPopover, InfoBloco, FILTRO } from '../../components/InfoPopover';
import { GlassPanel, KpiTile, PALETA, iconeDe } from '../_shared/glass';
import { UI } from '../../ui';

interface Props {
  cliente: IDossie['cliente'];
  scoreNum?: number;
  scoreClassif?: string;
  saudeTexto?: string;
  riscos: IDossieRisco[];
  colaboradores: number;
  porGenero: { sexo: string; qtd: number }[];
  onScreen: (screen: string) => void;
}

const humanize = (k: string) => k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

const fmtValor = (k: string, v: unknown): string => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return typeof v === 'string' ? v : '—';
  if (k.includes('pct') || k.includes('margem') || k.includes('carga')) return `${n.toFixed(1)}%`;
  if (Math.abs(n) >= 1000) return formatCurrency(n);
  return n.toLocaleString('pt-BR');
};

const corScore = (p: number): string => {
  if (p >= 80) return UI.pos;
  if (p >= 50) return UI.warn;
  return UI.neg;
};
const riscoColor = (s?: string): string => {
  if (s === 'red') return UI.neg;
  if (s === 'amber') return UI.warn;
  return UI.pos;
};
const corGenero = (sexo: string): string => {
  const s = (sexo || '').toUpperCase();
  if (s.startsWith('F')) return '#EC4899';
  if (s.startsWith('M')) return '#3B82F6';
  return '#94A3B8';
};

const MotionBox = motion(Box);

export function DossieClienteTab({
  cliente,
  scoreNum,
  scoreClassif,
  saudeTexto,
  riscos,
  colaboradores,
  porGenero,
  onScreen,
}: Props) {
  const resumoEnt = useMemo(
    () => Object.entries(cliente?.resumo ?? {}).filter(([, v]) => v !== null && v !== undefined),
    [cliente],
  );

  // FAIXA 1 — 4 KPIs HUD curados
  const kpis = useMemo(() => {
    const findKey = (re: RegExp, avoidPct = false) =>
      resumoEnt.find(([k]) => re.test(k.toLowerCase()) && (!avoidPct || !k.includes('pct')));
    const cargaPct = resumoEnt.find(([k]) => /carga.*pct|pct.*carga|carga_tribut/.test(k));
    const out: {
      label: string;
      value: string;
      sub?: string;
      icon: typeof CurrencyDollar;
      color: string;
    }[] = [];
    const fat = findKey(/fatur|receita/);
    if (fat)
      out.push({
        label: 'Faturamento',
        value: fmtValor(fat[0], fat[1]),
        sub: 'no período',
        icon: CurrencyDollar,
        color: UI.brand,
      });
    const imp = findKey(/imposto/, true) ?? findKey(/carga|tribut/);
    if (imp)
      out.push({
        label: 'Impostos',
        value: fmtValor(imp[0], imp[1]),
        sub: cargaPct ? `carga ${num(cargaPct[1]).toFixed(1)}%` : undefined,
        icon: Receipt,
        color: UI.warn,
      });
    const res = findKey(/result|margem|lucro/);
    if (res)
      out.push({
        label: 'Resultado',
        value: fmtValor(res[0], res[1]),
        icon: TrendUp,
        color: num(res[1]) >= 0 ? UI.pos : UI.neg,
      });
    const fol = findKey(/folha|pessoal|empreg/);
    if (fol)
      out.push({
        label: 'Custo de folha',
        value: fmtValor(fol[0], fol[1]),
        sub: colaboradores ? `${colaboradores} colaboradores` : undefined,
        icon: UsersThree,
        color: '#8B5CF6',
      });
    return out;
  }, [resumoEnt, colaboradores]);

  // FAIXA 2A — evolução (vendas × impostos)
  const evolucao = useMemo(
    () =>
      (cliente?.evolucao ?? []).map(e => ({
        mes: e.mes,
        vendas: num(e.vendas),
        compras: num(e.compras),
        impostos: num(e.impostos),
      })),
    [cliente],
  );

  // FAIXA 2B — carga por categoria (barra empilhada)
  const carga = useMemo(() => {
    const arr = (cliente?.carga_por_categoria ?? [])
      .map(c => ({ categoria: c.categoria, valor: num(c.valor) }))
      .filter(c => c.valor > 0)
      .sort((a, b) => b.valor - a.valor);
    const head = arr.slice(0, 6);
    const tail = arr.slice(6);
    if (tail.length)
      head.push({ categoria: 'Outros', valor: tail.reduce((s, c) => s + c.valor, 0) });
    const total = head.reduce((s, c) => s + c.valor, 0);
    return { itens: head, total };
  }, [cliente]);

  // FAIXA 3 — concentração + ranking
  const top = useMemo(
    () =>
      (cliente?.top_clientes ?? [])
        .map(c => ({ nome: c.nome ?? '—', uf: c.uf, total: num(c.total), pct: num(c.pct_total) }))
        .sort((a, b) => b.total - a.total),
    [cliente],
  );
  const donut = useMemo(() => {
    const t5 = top.slice(0, 5).map(c => ({ name: c.nome, value: c.total }));
    const rest = top.slice(5).reduce((s, c) => s + c.total, 0);
    if (rest > 0) t5.push({ name: 'Outros', value: rest });
    return t5;
  }, [top]);
  const maxTop = Math.max(...top.map(c => c.total), 1);

  // FAIXA 4 — saúde financeira
  const saude = useMemo(
    () =>
      Object.entries(cliente?.saude_financeira ?? {}).filter(
        ([, v]) => v !== null && v !== undefined,
      ),
    [cliente],
  );

  // FAIXA 5 — equipe
  const totalGen = porGenero.reduce((s, g) => s + num(g.qtd), 0);
  const genDonut = porGenero.map(g => ({ name: g.sexo, value: num(g.qtd) }));
  const folha = useMemo(
    () =>
      Object.entries(cliente?.equipe?.folha ?? {}).filter(([, v]) => v !== null && v !== undefined),
    [cliente],
  );

  if (!cliente) {
    return <EmptyState title="Sem dados de cliente no dossiê." message="" icon={Files} />;
  }

  const scoreP = Math.max(0, Math.min(100, scoreNum ?? 0));
  const scoreC = corScore(scoreP);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* FAIXA 0 — saúde */}
      {(scoreNum !== undefined || saudeTexto || riscos.length > 0) && (
        <Box mb={5}>
          <GlassPanel>
            <Flex gap={5} align="center" wrap={{ base: 'wrap', lg: 'nowrap' }}>
              {scoreNum !== undefined && scoreNum !== null && (
                <Box position="relative" w="120px" h="92px" flexShrink={0}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart
                      innerRadius="72%"
                      outerRadius="100%"
                      data={[{ v: scoreP }]}
                      startAngle={180}
                      endAngle={0}
                    >
                      <PolarAngleAxis
                        type="number"
                        domain={[0, 100]}
                        angleAxisId={0}
                        tick={false}
                      />
                      <RadialBar
                        background
                        dataKey="v"
                        cornerRadius={8}
                        fill={scoreC}
                        angleAxisId={0}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                  <Flex
                    position="absolute"
                    inset="0"
                    direction="column"
                    align="center"
                    justify="center"
                    pt="14px"
                    pointerEvents="none"
                  >
                    <Text fontSize="28px" fontWeight={900} color={UI.ink} lineHeight="1">
                      {Math.round(scoreP)}
                    </Text>
                    <Text
                      fontSize="9px"
                      fontWeight={800}
                      color={scoreC}
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                    >
                      {scoreClassif ?? 'Score'}
                    </Text>
                  </Flex>
                </Box>
              )}
              <Box flex={1} minW="200px">
                <Text
                  fontSize="10px"
                  fontWeight={800}
                  color={UI.faint}
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  mb={1}
                >
                  Saúde da empresa
                </Text>
                <Text fontSize="15px" fontWeight={800} color={UI.ink} lineHeight="1.35">
                  {saudeTexto ?? 'Sem resumo de saúde no período.'}
                </Text>
              </Box>
              {riscos.length > 0 && (
                <Flex
                  direction="column"
                  gap={1.5}
                  flexShrink={0}
                  minW={{ base: '100%', md: '220px' }}
                >
                  {riscos.slice(0, 3).map(r => {
                    const c = riscoColor(String(r.status));
                    return (
                      <Flex
                        key={`${r.label}-${String(r.valor)}`}
                        align="center"
                        gap={2}
                        px={2.5}
                        py={1.5}
                        borderRadius="10px"
                        style={{ background: `${c}14` }}
                      >
                        <Box
                          w="8px"
                          h="8px"
                          borderRadius="full"
                          style={{ background: c }}
                          flexShrink={0}
                        />
                        <Text fontSize="12px" color={UI.ink} flex={1} noOfLines={1}>
                          {r.label}
                        </Text>
                        <Text
                          fontSize="12px"
                          fontWeight={800}
                          color={UI.ink}
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {typeof r.valor === 'object'
                            ? '—'
                            : fmtValor(String(r.label ?? ''), r.valor)}
                        </Text>
                      </Flex>
                    );
                  })}
                </Flex>
              )}
            </Flex>
          </GlassPanel>
        </Box>
      )}

      {/* FAIXA 1 — KPIs HUD */}
      {kpis.length > 0 && (
        <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={5}>
          {kpis.map(k => (
            <KpiTile
              key={k.label}
              label={k.label}
              value={k.value}
              sub={k.sub}
              icon={k.icon}
              color={k.color}
              info={
                <InfoPopover title={k.label}>
                  <InfoBloco
                    oque={`${k.label} consolidado da empresa no período.`}
                    dados="Resumo do dossiê (extract_* da empresa)"
                    filtro={FILTRO.empresa}
                  />
                </InfoPopover>
              }
            />
          ))}
        </SimpleGrid>
      )}

      {/* FAIXA 2 — evolução | carga */}
      <Grid templateColumns={{ base: '1fr', lg: '1.4fr 1fr' }} gap={5} mb={5}>
        <GlassPanel title="Evolução do período" sub="vendas × impostos">
          {evolucao.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={evolucao}>
                <defs>
                  <linearGradient id="cliFat" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={UI.brand} stopOpacity={0.32} />
                    <stop offset="100%" stopColor={UI.brand} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
                <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
                <YAxis
                  fontSize={11}
                  stroke={UI.muted}
                  width={64}
                  tickFormatter={v => formatCurrency(v)}
                />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="vendas"
                  name="Vendas"
                  stroke={UI.brand}
                  fill="url(#cliFat)"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="impostos"
                  name="Impostos"
                  stroke={UI.warn}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <Text fontSize="12.5px" color={UI.muted} py={6} textAlign="center">
              Sem série de evolução no período.
            </Text>
          )}
        </GlassPanel>

        <GlassPanel title="Carga tributária por categoria">
          {carga.itens.length > 0 ? (
            <Box>
              <Flex
                h="22px"
                borderRadius="full"
                overflow="hidden"
                boxShadow="inset 0 1px 3px rgba(15,23,42,0.12)"
                mb={4}
              >
                {carga.itens.map((c, i) => (
                  <Box
                    key={c.categoria}
                    h="100%"
                    style={{
                      width: `${(c.valor / carga.total) * 100}%`,
                      background: c.categoria === 'Outros' ? '#94A3B8' : PALETA[i % PALETA.length],
                    }}
                  />
                ))}
              </Flex>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={1.5}>
                {carga.itens.map((c, i) => (
                  <Flex key={c.categoria} align="center" gap={2}>
                    <Box
                      w="9px"
                      h="9px"
                      borderRadius="2px"
                      flexShrink={0}
                      style={{
                        background:
                          c.categoria === 'Outros' ? '#94A3B8' : PALETA[i % PALETA.length],
                      }}
                    />
                    <Text fontSize="11.5px" color={UI.ink} flex={1} noOfLines={1}>
                      {c.categoria}
                    </Text>
                    <Text
                      fontSize="11.5px"
                      fontWeight={700}
                      color={UI.ink}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatCurrency(c.valor)}
                    </Text>
                    <Text fontSize="10.5px" color={UI.faint} w="38px" textAlign="right">
                      {pct((c.valor / carga.total) * 100)}
                    </Text>
                  </Flex>
                ))}
              </SimpleGrid>
            </Box>
          ) : (
            <Text fontSize="12.5px" color={UI.muted} py={6} textAlign="center">
              Sem dados de carga por categoria.
            </Text>
          )}
        </GlassPanel>
      </Grid>

      {/* FAIXA 3 — concentração | ranking */}
      {top.length > 0 && (
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5} mb={5}>
          <GlassPanel title="Concentração da carteira" sub="participação no faturamento">
            <Box position="relative">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={donut}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={92}
                    paddingAngle={2}
                  >
                    {donut.map((entry, i) => (
                      <Cell
                        key={entry.name}
                        fill={entry.name === 'Outros' ? '#94A3B8' : PALETA[i % PALETA.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
              <Flex
                position="absolute"
                inset="0"
                direction="column"
                align="center"
                justify="center"
                pointerEvents="none"
              >
                <Text fontSize="24px" fontWeight={900} color={UI.ink} lineHeight="1">
                  {pct(top[0]?.pct ?? 0)}
                </Text>
                <Text
                  fontSize="10px"
                  color={UI.muted}
                  textTransform="uppercase"
                  letterSpacing="0.4px"
                >
                  maior cliente
                </Text>
              </Flex>
            </Box>
          </GlassPanel>

          <GlassPanel
            title="Top clientes"
            action={
              <Button
                size="xs"
                variant="ghost"
                color={UI.brand}
                rightIcon={<ArrowSquareOut size={12} weight="bold" />}
                onClick={() => onScreen('clientes-fornecedores')}
              >
                abrir
              </Button>
            }
          >
            <Flex direction="column" gap={2.5}>
              {top.slice(0, 8).map(c => (
                <Flex key={`${c.nome}-${c.total}`} align="center" gap={3}>
                  <Box flex={1} minW={0}>
                    <Text fontSize="12px" fontWeight={700} color={UI.ink} noOfLines={1}>
                      {c.nome}
                    </Text>
                    <Flex
                      h="6px"
                      bg="rgba(15,23,42,0.06)"
                      borderRadius="full"
                      overflow="hidden"
                      mt={1}
                    >
                      <Box
                        h="100%"
                        borderRadius="full"
                        style={{
                          width: `${(c.total / maxTop) * 100}%`,
                          background: `linear-gradient(to right, ${UI.brand}, #1AA6B7)`,
                        }}
                      />
                    </Flex>
                  </Box>
                  <Text
                    fontSize="12.5px"
                    fontWeight={800}
                    color={UI.ink}
                    w="92px"
                    textAlign="right"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatCurrency(c.total)}
                  </Text>
                  <Text
                    fontSize="11px"
                    color={c.pct >= 30 ? UI.warn : UI.muted}
                    w="44px"
                    textAlign="right"
                  >
                    {pct(c.pct)}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </GlassPanel>
        </Grid>
      )}

      {/* FAIXA 4 — saúde financeira | equipe */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5} mb={5}>
        {saude.length > 0 && (
          <GlassPanel
            title="Saúde financeira"
            action={
              <Button
                size="xs"
                variant="ghost"
                color={UI.brand}
                rightIcon={<ArrowSquareOut size={12} weight="bold" />}
                onClick={() => onScreen('contabil')}
              >
                Contábil
              </Button>
            }
          >
            <Box>
              {saude.map(([k, v]) => (
                <Flex
                  key={k}
                  justify="space-between"
                  align="center"
                  py={2}
                  borderBottom="1px solid rgba(15,23,42,0.06)"
                >
                  <Flex align="center" gap={2}>
                    <Flex
                      align="center"
                      justify="center"
                      w="22px"
                      h="22px"
                      borderRadius="6px"
                      bg={UI.infoSoft}
                      color={UI.brand}
                    >
                      <Icon as={iconeDe(k)} boxSize="12px" weight="bold" />
                    </Flex>
                    <Text fontSize="12.5px" color={UI.muted}>
                      {humanize(k)}
                    </Text>
                  </Flex>
                  <Text
                    fontSize="13px"
                    fontWeight={800}
                    color={UI.ink}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {fmtValor(k, v)}
                  </Text>
                </Flex>
              ))}
            </Box>
          </GlassPanel>
        )}

        {(totalGen > 0 || folha.length > 0) && (
          <GlassPanel
            title="Equipe"
            action={
              <Button
                size="xs"
                variant="ghost"
                color={UI.brand}
                rightIcon={<ArrowSquareOut size={12} weight="bold" />}
                onClick={() => onScreen('pessoal')}
              >
                Pessoal
              </Button>
            }
          >
            <Flex gap={4} align="center" wrap={{ base: 'wrap', sm: 'nowrap' }}>
              {totalGen > 0 && (
                <Box position="relative" w="120px" h="120px" flexShrink={0}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={genDonut}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={38}
                        outerRadius={56}
                        paddingAngle={2}
                      >
                        {genDonut.map(g => (
                          <Cell key={g.name} fill={corGenero(g.name)} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <Flex
                    position="absolute"
                    inset="0"
                    direction="column"
                    align="center"
                    justify="center"
                    pointerEvents="none"
                  >
                    <Text fontSize="20px" fontWeight={900} color={UI.ink} lineHeight="1">
                      {totalGen}
                    </Text>
                    <Text fontSize="9px" color={UI.muted} textTransform="uppercase">
                      pessoas
                    </Text>
                  </Flex>
                </Box>
              )}
              <Box flex={1} minW="160px">
                {folha.slice(0, 4).map(([k, v]) => (
                  <Flex
                    key={k}
                    justify="space-between"
                    py={1.5}
                    borderBottom="1px solid rgba(15,23,42,0.06)"
                  >
                    <Text fontSize="12px" color={UI.muted}>
                      {humanize(k)}
                    </Text>
                    <Text
                      fontSize="12.5px"
                      fontWeight={700}
                      color={UI.ink}
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {fmtValor(k, v)}
                    </Text>
                  </Flex>
                ))}
                {porGenero.length > 0 && (
                  <Flex gap={3} mt={2} wrap="wrap">
                    {porGenero.map(g => (
                      <Flex key={g.sexo} align="center" gap={1.5}>
                        <Box
                          w="9px"
                          h="9px"
                          borderRadius="full"
                          style={{ background: corGenero(g.sexo) }}
                        />
                        <Text fontSize="11px" color={UI.muted}>
                          {g.sexo}: <b>{g.qtd}</b>
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Box>
            </Flex>
          </GlassPanel>
        )}
      </Grid>
    </MotionBox>
  );
}
